import { connect } from "react-redux"
import { me } from "../../actions/user"
import { getRecommendedServices } from "../../actions/services"
import { withRouter } from "react-router"
import { Divider, Modal } from "semantic-ui-react"
import { ReactComponent as CarAdvise } from "../../../images/CarAdvise-Logo.svg"
import { useEffect } from "react"
import { formatAmount, monthDateFormat } from "./ViewReceipt"
import { faClose } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CREDIT_CARD_TYPE } from "caradvise_shared_components/lib/constants/payment_providers"
import { PAYPAL } from "../../constants/tires"
import { numberFormatToCurrency } from "../../../shared_component/utils/currency"
import {
  getCompletedServices,
  getReceiptPricing,
  promotionsSummaryLabel
} from "../../helpers/orderHelpers"

const ReceiptPrintPreview = ({ order = {}, open, close }) => {
  const vehicle = order?.vehicle || {}
  const shop = order?.shop || {}
  const paymentMethod = order?.braintree_payment_methods?.[0] || {}
  const pricing = getReceiptPricing(order)

  useEffect(() => {
    let root
    if (open) {
      root = document.getElementById("root")
    }

    if (root) {
      root.style.display = "none"
    }

    return () => {
      if (root) {
        root.style.display = "block"
      }
    }
  }, [open])

  return (
    <Modal open={open} className="receipt-preview modal">
      <div className="receipt-preview">
        <div className="header">
          <p className="header__title">Print Preview</p>
          <FontAwesomeIcon icon={faClose} onClick={close} />
        </div>

        <Divider />

        <div className="section">
          <div className="flex-between solid-border-bottom" style={{ paddingBottom: "8px" }}>
            <div>
              <CarAdvise />
              <p className="text--light text--sm">Service Request</p>
            </div>

            <div>
              <p className="text--gray text--sm text--semibold text--right">Order ID:</p>
              <p className="text--gray text--sm text--semibold text--right">{order.id}</p>
              <p className="text--light text--xs text--right">
                Date: {monthDateFormat(order.appointment_datetime)}
              </p>
            </div>
          </div>

          <div className="flex gap-24">
            <div className="column gap-12">
              <p className="section-title">Vehicle</p>

              <div className="column gap-8">
                <div>
                  <p className="text--light text--sm text--semibold">Vehicle</p>
                  <p className="text--dark text--sm">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </p>
                </div>

                <div>
                  <p className="text--light text--sm text--semibold">Mileage</p>
                  <p className="text--dark text--sm">
                    {vehicle?.odometer_config?.display_value_abbr}
                  </p>
                </div>
              </div>
            </div>

            <div className="column gap-12" style={{ width: "60%" }}>
              <p className="section-title">Shop</p>

              <div>
                <p className="text--dark text--sm text--bold">{shop.name}</p>
                <p className="text--gray text--sm">{shop.address_line1}</p>
                <p className="text--gray text--sm">
                  {shop.city}, {shop.state}
                </p>
                <p className="text--gray text--sm">{shop.zip}</p>
              </div>
            </div>
          </div>

          <div className="column gap-16">
            <p className="section-title">Services completed</p>

            <div className="column gap-24">
              <div className="service-header">
                <p className="text--gray text--sm">Service Description</p>
                <p className="text--gray text--sm">Amount</p>
              </div>

              {getCompletedServices(order).map((service, index) => (
                <div key={index} className="service-row">
                  <p className="text--gray text--sm">
                    {service.name} {service.position ? `(${service.position})` : ""}
                  </p>
                  <p className="text--gray text--sm">
                    {numberFormatToCurrency(Number(service.override_total || 0).toFixed(2))}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="column gap-16">
            <div className="total-row">
              <p className="text--dark text--sm text--bold">Services total</p>
              <p className="text--dark text--sm text--bold">
                {formatAmount(pricing.servicesTotal)}
              </p>
            </div>

            {pricing.promotionalDiscount > 0 ? (
              <div className="total-row">
                <p className="text--gray text--sm">{promotionsSummaryLabel(order)}</p>
                <p className="text--dark text--sm text--semibold">
                  -{formatAmount(pricing.promotionalDiscount)}
                </p>
              </div>
            ) : null}

            {pricing.promotionalDiscount > 0 ? (
              <>
                <div className="light-border-bottom" />
                <div className="total-row">
                  <p className="text--dark text--sm text--bold">Total after promos</p>
                  <p className="text--dark text--sm text--bold">
                    {formatAmount(pricing.totalAfterPromos)}
                  </p>
                </div>
              </>
            ) : null}

            {pricing.membershipDiscount > 0 ? (
              <div className="total-row">
                <p className="text--gray text--sm">
                  Membership discount
                  {pricing.membershipPercent > 0 ? ` (${pricing.membershipPercent}%)` : ""}
                </p>
                <p className="text--dark text--sm text--semibold">
                  -{formatAmount(pricing.membershipDiscount)}
                </p>
              </div>
            ) : null}

            {pricing.rewardsDiscount > 0 ? (
              <div className="total-row">
                <p className="text--rewards text--sm">Rewards applied</p>
                <p className="text--rewards text--sm text--semibold">
                  -{formatAmount(pricing.rewardsDiscount)}
                </p>
              </div>
            ) : null}

            {pricing.membershipDiscount > 0 || pricing.rewardsDiscount > 0 ? (
              <>
                <div className="light-border-bottom" />
                <div className="total-row">
                  <p className="text--dark text--sm text--bold">Total after promos & Discounts</p>
                  <p className="text--dark text--sm text--bold">{formatAmount(pricing.subTotal)}</p>
                </div>
              </>
            ) : null}

            <div className="total-row">
              <p className="text--gray text--sm">Taxes on services total</p>
              <p className="text--dark text--sm text--semibold">{formatAmount(pricing.taxes)}</p>
            </div>

            {pricing.surchargePercent > 0 ? (
              <div className="total-row">
                <p className="text--gray text--sm">
                  Out-Of-Network fee on services ({pricing.surchargePercent}%)
                </p>
                <p className="text--dark text--sm text--semibold">
                  {pricing.networkSurcharge > 0
                    ? formatAmount(pricing.networkSurcharge)
                    : `${pricing.surchargePercent}%`}
                </p>
              </div>
            ) : null}

            <div className="total-row">
              <p className="text--gray text--sm">Transaction fee</p>
              <p className="text--dark text--sm text--semibold">
                {formatAmount(order.transaction_fee)}
              </p>
            </div>

            <div className="solid-border-bottom" />

            <div className="total-row">
              <p className="text--dark text--md text--semibold">Total Paid</p>
              <p className="text--dark text--md text--semibold">
                {formatAmount(order.estimated_total)}
              </p>
            </div>

            {Number(order.refunded_amount || 0) > 0 ? (
              <div className="total-row">
                <p className="text--gray text--sm">Total Refund</p>
                <p className="text--dark text--sm text--semibold">
                  {formatAmount(order.refunded_amount)}
                </p>
              </div>
            ) : null}

            {Number(order.estimated_savings) > 0 ? (
              <div className="savings-box">
                <p className="savings-title">Total Saved with CarAdvise</p>
                <p className="savings-total">
                  {numberFormatToCurrency(Number(order.estimated_savings).toFixed(2))}
                </p>
              </div>
            ) : null}
          </div>

          {paymentMethod.type && (
            <div className="column gap-12">
              <p className="section-title">Payment type</p>
              {paymentMethod.type === CREDIT_CARD_TYPE && (
                <p className="text--gray text--sm">
                  {paymentMethod.card_type}****{paymentMethod.last_4}
                </p>
              )}
              {paymentMethod.type === PAYPAL && (
                <p className="text--gray text--sm">PayPal {paymentMethod.email}</p>
              )}
            </div>
          )}

          <p className="text--center text--xs text--light">
            Shop-issued receipts may not display your CarAdvise pricing or applied discounts. Use
            this receipt as your official proof of payment.
          </p>
        </div>
      </div>
    </Modal>
  )
}

function mapStateToProps(state, props) {
  return {}
}

export default connect(mapStateToProps, { me, getRecommendedServices })(
  withRouter(ReceiptPrintPreview)
)
